import React, { useState, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Flex, Link
} from 'components/ui';
import { getLocationHoursText } from 'utils/AccessPoints';
import AccessPointModal from 'components/FrictionlessCheckout/DeliverToReleaseThree/AccessPoint/AccessPointModal';
import orderUtils from 'utils/Order';
import { lineHeights } from 'style/config';
import Checkout from 'services/api/checkout';

const { getAccessPointHours } = Checkout;

function HalAddress({ address, halOperatingHours, holdAtLocation, isDraft }) {
    const {
        address1, city, state, postalCode, altPickLocationID
    } = address;
    const [operatingHours, setOperatingHours] = useState(halOperatingHours);

    useEffect(() => {
        // Fetch operating hours if not already provided
        if (!halOperatingHours?.length && postalCode && altPickLocationID) {
            getAccessPointHours({
                zipCode: postalCode,
                altPickLocationID
            })
                .then(location => {
                    const { operatingHours: fetchedHours } = location;
                    setOperatingHours(fetchedHours);
                })
                .catch(() => {
                    setOperatingHours(undefined);
                });
        }
    }, [halOperatingHours, postalCode, altPickLocationID]);

    const locationHoursText = getLocationHoursText(operatingHours);

    return (
        <Box
            width='65%'
            {...(isDraft && { paddingTop: 3 })}
            marginBottom={1}
        >
            <Box>
                <Text
                    is='p'
                    fontSize='sm'
                    children={holdAtLocation}
                    display='inline-block'
                />
            </Box>

            <Box>
                <Text
                    is='p'
                    fontSize='base'
                    fontWeight='bold'
                    children={address.halCompany}
                    lineHeight={lineHeights.tight}
                    marginBottom='2px'
                />

                <Text
                    is='p'
                    fontSize='base'
                    children={address1}
                    lineHeight={lineHeights.tight}
                    marginBottom='2px'
                />
                <Text
                    is='p'
                    fontSize='base'
                    children={`${city}, ${state} ${postalCode}`}
                    lineHeight={lineHeights.tight}
                />

                <Text
                    is='p'
                    marginTop={1}
                    fontSize='sm'
                    children={locationHoursText}
                />
            </Box>
        </Box>
    );
}

function ShippingAddress({
    address, showEdit, onChangeClick, localization, changeShippingAddress, shouldDisplayEmailAddress
}) {
    const {
        firstName, lastName, address1, address2, city, state, postalCode, email
    } = address;

    return (
        <Flex justifyContent='space-between'>
            <Box>
                <Text
                    is='p'
                    fontWeight='bold'
                    fontSize={['base', 'base', 'md']}
                    children={`${firstName} ${lastName}`}
                />
                <Text
                    is='p'
                    fontSize={['base', 'base', 'md']}
                    children={address1}
                />
                <Text
                    is='p'
                    fontSize={['base', 'base', 'md']}
                    children={address2}
                />
                <Text
                    is='p'
                    fontSize={['base', 'base', 'md']}
                    children={`${city ? city + ' ' : ''}${state} ${postalCode}`}
                />
                {shouldDisplayEmailAddress && (
                    <Text
                        is='p'
                        css={styles.emailStyles}
                        fontSize={['base', 'base', 'md']}
                        children={email}
                    />
                )}
            </Box>
            {showEdit && (
                <Box>
                    <Link
                        color='link'
                        lineHeight='tight'
                        children={localization.edit}
                        onClick={() => onChangeClick(true)}
                        aria-label={changeShippingAddress}
                    />
                </Box>
            )}
        </Flex>
    );
}

function DisplayAccessPoint({ accessPointCallback, edit }) {
    const [showAccessPointModal, setShowAccessPointModal] = useState(false);

    const toggleShowAccesPointModal = () => {
        setShowAccessPointModal(prevState => !prevState);
    };

    const setAccessPoint = accessPoint => {
        const address = {
            addressType: orderUtils.SHIPPING_METHOD_TYPES.HAL,
            address1: accessPoint?.addressLine1,
            city: accessPoint?.city,
            state: accessPoint?.state,
            postalCode: accessPoint?.zipCode,
            country: accessPoint?.country,
            altPickLocationID: accessPoint?.locationId,
            altPickLocationType: accessPoint?.pickupLocationType,
            altPickLocationPartner: accessPoint?.carrierFixed,
            altLocationPhone: accessPoint?.phoneNumber,
            halCompany: accessPoint?.companyName
        };

        const halOperatingHours = accessPoint?.operatingHours;
        accessPointCallback(true, false, { ...address, halOperatingHours });
    };

    return (
        <>
            {showAccessPointModal && (
                <AccessPointModal
                    isOpen={showAccessPointModal}
                    onDismiss={toggleShowAccesPointModal}
                    callback={setAccessPoint}
                />
            )}

            <Link
                color='blue'
                lineHeight='tight'
                children={edit}
                onClick={toggleShowAccesPointModal}
            />
        </>
    );
}

function AddressDisplay({
    address,
    isHal,
    children,
    halOperatingHours,
    holdAtLocation,
    showEdit,
    onChangeClick,
    localization,
    changeShippingAddress,
    isDraft = false,
    callback,
    isGiftCard,
    shouldDisplayEmailAddress
}) {
    return (
        <>
            <Box css={{ textTransform: 'capitalize' }}>
                {isHal ? (
                    <Flex
                        flexDirection={isDraft ? 'column' : 'row'}
                        {...(!isDraft && { justifyContent: 'space-between' })}
                        alignItems='flex-start'
                    >
                        <HalAddress
                            address={address}
                            halOperatingHours={halOperatingHours}
                            holdAtLocation={holdAtLocation}
                            isDraft={isDraft}
                        />
                        {children}

                        {!isDraft && (
                            <DisplayAccessPoint
                                accessPointCallback={callback}
                                edit={localization.edit}
                            />
                        )}
                    </Flex>
                ) : (
                    <ShippingAddress
                        address={address}
                        showEdit={showEdit}
                        onChangeClick={onChangeClick}
                        localization={localization}
                        changeShippingAddress={changeShippingAddress}
                        isGiftCard={isGiftCard}
                        shouldDisplayEmailAddress={shouldDisplayEmailAddress}
                    />
                )}
            </Box>
            {!isHal && children}
        </>
    );
}

const styles = {
    emailStyles: {
        textTransform: 'none'
    }
};

export default wrapFunctionalComponent(AddressDisplay, 'AddressDisplay');
